import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { analytics } from "../../analytics/analytics";
import { api } from "../../api/client";
import { Button, Icon, PageHeader, Tag } from "../../components/ui";
import { t } from "../../i18n";
import { openExternal } from "../../platform/device";
export function ReservationPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const query = useQuery({
    queryKey: ["reservations"],
    queryFn: api.reservations,
    refetchInterval: 10000,
  });
  const reservation = query.data?.find((item) => item.id === id);
  async function pay(requestId: string) {
    const action = await api.createPayment(
      requestId,
      "spotit://payment-return",
    );
    analytics.track("payment_action_opened");
    if (action.redirectUrl) await openExternal(action.redirectUrl);
  }
  if (query.isSuccess && !reservation)
    return (
      <div className="page">
        <PageHeader back={() => navigate(-1)} title="Reservation not found" />
        <p>
          This reservation may still be processing. Return to Plans & bookings
          and refresh.
        </p>
      </div>
    );
  return (
    <div className="page">
      <PageHeader
        back={() => navigate(-1)}
        eyebrow="LIVE STATUS"
        title={
          reservation?.stages.length === 1
            ? "Reservation status"
            : "Your plan status"
        }
      />
      <div className="status-banner">
        <span className="pulse" />
        <div>
          <strong>
            {reservation?.stages.length === 1
              ? t.directBooking.pendingTitle
              : "Booking requests update independently"}
          </strong>
          <p>
            {reservation?.stages.length === 1
              ? t.directBooking.pendingBody
              : "You can safely leave this page and return later."}
          </p>
        </div>
      </div>
      {reservation?.walkInStages.map((stage) => (
        <article className="reservation-stage walk-in-stage" key={stage.id}>
          <div className="reservation-stage__marker">
            <Icon name="check" />
          </div>
          <div>
            <small>{formatTime(stage.startsAt)}</small>
            <h2>{stage.offeringName}</h2>
            <p>{stage.venueName}</p>
            <Tag tone="success">
              {stage.bookingMode === "walk_in"
                ? t.booking.walk_in
                : t.booking.no_booking_required}
            </Tag>
            <p className="fine-print">
              Part of your itinerary. No venue confirmation is required.
            </p>
          </div>
        </article>
      ))}
      <div className="reservation-timeline">
        {reservation?.stages.map((stage, index) => (
          <article key={stage.venueRequestId} className="reservation-stage">
            <div className="reservation-stage__marker">
              {stage.status === "confirmed" ? <Icon name="check" /> : index + 1}
            </div>
            <div>
              <small>{formatTime(stage.startsAt)}</small>
              <h2>{stage.venueName}</h2>
              <p>{stage.branchName}</p>
              <Tag
                tone={
                  stage.status === "confirmed"
                    ? "success"
                    : stage.status === "replacement_required" ||
                        stage.status === "rejected"
                      ? "danger"
                      : "warning"
                }
              >
                {t.booking[stage.status]}
              </Tag>
              <div className="payment-box">
                <div>
                  <small>DEPOSIT</small>
                  <strong>
                    ${(stage.depositMinor / 100).toFixed(2)} {stage.currency}
                  </strong>
                </div>
                <div>
                  <small>PAYMENT</small>
                  <strong>{stage.paymentStatus.replaceAll("_", " ")}</strong>
                </div>
              </div>
              {stage.status === "accepted_deposit_required" && (
                <Button onClick={() => void pay(stage.venueRequestId)}>
                  Pay with Whish
                </Button>
              )}
              {stage.status === "replacement_required" && (
                <>
                  <p className="danger-copy">
                    This request needs a replacement. Confirmed bookings remain
                    unchanged.
                  </p>
                  <Button onClick={() => navigate("/plans/plan-mixed")}>
                    Choose a replacement
                  </Button>
                </>
              )}
            </div>
          </article>
        ))}
      </div>
      <p className="fine-print">
        A redirect alone never confirms payment. Spot waits for server
        confirmation from Whish.
      </p>
    </div>
  );
}
const formatTime = (value: string) =>
  new Intl.DateTimeFormat("en-LB", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "Asia/Beirut",
  }).format(new Date(value));
